import React, { useEffect, useState } from 'react'
import DataTable from 'react-data-table-component'
import { getStageStatus, renderStatus } from '../../utils/helpers'
import BadgeDropdown from './BadgeDropdown'
import PrimaryButton from '../buttons/PrimaryButton'
import TableSkeleton from './TableSkeleton'
import { useBlocker } from 'react-router-dom'
import OverlayModal from './OverlayModal'
import OutlineButton from '../buttons/OutlineButton'

const CourseTable = ({
  loading,
  stages,
  page,
  rowsPerPage,
  totalRows,
  handlePageChange,
  handlePerRowsChange,
  handleAssignBadge,
  assignLoading,
}) => {
  const [selectedBadges, setSelectedBadges] = useState({})
  const [changedBadges, setChangedBadges] = useState({})
  const [initialSelected, setInitialSelected] = useState({})
  const [unSavedChanges, setUnSavedChanges] = useState(false)
  const [navigateConfirmModal, setNavigateConfirmModal] = useState(false)

  // Preselect badges where earned_at is not null
  useEffect(() => {
    const initializeSelectedBadges = () => {
      const initialSelectedTemp = {}
      stages.forEach((stage) => {
        const earnedBadges = stage.stage_badges
          .filter(
            (badge) =>
              badge.user_badge_progress &&
              badge.user_badge_progress.length > 0 &&
              badge.user_badge_progress[0].earned_at !== null
          )
          .map((badge) => badge.id) // Get the IDs of earned badges

        const earnedCourseBadges = stage.courses.course_badges
          .filter(
            (badge) =>
              badge.user_badge_progress &&
              badge.user_badge_progress.length > 0 &&
              badge.user_badge_progress[0].earned_at !== null
          )
          .map((badge) => badge.id)

        initialSelectedTemp[stage.id] = {
          stage: earnedBadges,
          course: earnedCourseBadges
        }
      })

      setSelectedBadges(initialSelectedTemp)
      setInitialSelected(initialSelectedTemp)
    }

    if (stages && stages.length > 0) {
      initializeSelectedBadges()
    }
  }, [stages])

  useEffect(() => {
    const changes = {};

    Object.keys(selectedBadges).forEach((key) => {
      const selectedStage = selectedBadges[key].stage || [];
      const initialStage = initialSelected[key]?.stage || [];
      const selectedCourse = selectedBadges[key].course || [];
      const initialCourse = initialSelected[key]?.course || [];

      const stageChanges = selectedStage.filter(badge => !initialStage.includes(badge));
      const courseChanges = selectedCourse.filter(badge => !initialCourse.includes(badge));

      if (stageChanges.length || courseChanges.length) {
        changes[key] = {
          stage: stageChanges,
          course: courseChanges,
        };
      }
    });

    const hasChanges = Object.keys(changes).length > 0;
    setUnSavedChanges(hasChanges)
    setChangedBadges(changes)
  }, [selectedBadges, initialSelected])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unSavedChanges) {
        e.preventDefault()
        e.returnValue = '' // required for chrome
        return true // Return a truthy value
      }
      return null // Allow navigation if no conditions met
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [unSavedChanges])

  const shouldBlockNavigation = () => {
    if (unSavedChanges) {
      setNavigateConfirmModal(true)
      return 'Are you sure you want to leave? You may lose unsaved changes.'
    }
    return null // Allow navigation
  }

  let blocker = useBlocker(shouldBlockNavigation)

  // Columns for the table
  const columns = [
    {
      name: 'Course',
      selector: (row) => row.courses.title,
      sortable: false,
    },
    {
      name: 'Stage name',
      selector: (row) => row.name,
      sortable: false,
      wrap: true,
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      cell: (row) => renderStatus(getStageStatus(row)),
      sortable: false,
    },
    {
      name: 'Badges',
      selector: (row) => getBadgeNames(row).join(', '),
      sortable: false,
    },
    {
      name: 'Assign badges',
      cell: (row) => (
        <BadgeDropdown
          disabled={getStageStatus(row) !== 'completed'}
          stageBadges={row.stage_badges}
          courseBadges={row?.courses?.course_badges || []}
          onSelect={(selectedIds) => handleBadgeSelect(selectedIds, row.id)}
          badgeSelected={selectedBadges[row.id] || []} // Pass selected badges per row
          disableEarnedBadges={true} // Disable earned badges
        />
      ),
      style: {
        minWidth: '200px',
      },
    },
  ]

  // Helper function to get badge names from stage
  const getBadgeNames = (stage) => {
    if (!stage || !stage.stage_badges || stage.stage_badges.length === 0) {
      return []
    }
    return stage.stage_badges.map((stage_badge) => stage_badge.badges.name)
  }

  // Handle badge selection and update the state
  const handleBadgeSelect = (selectedIds, stageId) => {
    const ids = selectedIds.reduce(
      (acc, selectedId) => {
        if (selectedId.isCourse) {
          acc.course.push(selectedId.value);
        } else {
          acc.stage.push(selectedId.value);
        }
        return acc;
      },
      { stage: [], course: [] }
    );

    setSelectedBadges((prevState) => ({
      ...prevState,
      [stageId]: ids,
    }))
  }

  return (
    <div>
      {navigateConfirmModal && (
        <OverlayModal
          isOpen={navigateConfirmModal}
          width="max-w-sm"
          onClose={() => setNavigateConfirmModal(false)}
        >
          <div className="flex flex-col items-center gap-2 justify-center text-center">
            <p className="text-black font-semibold text-lg">Unsaved Changes</p>
            <p className="text-black text-sm">
              You have unsaved changes. Are you sure you want to leave this
              page?
            </p>
            <PrimaryButton
              className="w-full"
              onClick={() => {
                handleAssignBadge(selectedBadges)
                setNavigateConfirmModal(false)
              }}
            >
              Save
            </PrimaryButton>
            <OutlineButton
              className="w-full"
              onClick={() => blocker?.proceed()}
            >
              Leave without saving
            </OutlineButton>
          </div>
        </OverlayModal>
      )}

      {loading ? (
        <TableSkeleton rows={rowsPerPage} cols={5} />
      ) : (
        <DataTable
          columns={columns}
          data={stages}
          pagination
          paginationServer
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          paginationDefaultPage={page}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      )}

      <div className="flex justify-end w-full mt-2">
        <PrimaryButton
          disabled={!unSavedChanges || assignLoading}
          className="w-fit"
          onClick={() => {
            handleAssignBadge(changedBadges)
          }}
        >
          Save
        </PrimaryButton>
      </div>
    </div>
  )
}

export default CourseTable
